import { getToken } from '../utils/auth'
import { getMidwayToken } from '../utils/midway'
import { apiKeyService } from './apiKeyService'

// Enhanced interface for IRIS requests with better typing
interface IRISRequest {
  userMessage: string;
  stage: 'intake' | 'summarization' | 'finaljson' | 'explore';
  agentId: string;
  intakeData?: any;
  sessionId?: string;
  // Explore mode specific fields
  explorePhase?: number;
  explorePhaseData?: any;
  phaseName?: string;
}

// Enhanced interface for IRIS responses with structured data
interface IRISResponse {
  // The conversational reply from IRIS
  reply: string;
  
  // Structured data extracted from the conversation
  extractedData?: {
    title?: string;
    problem_statement?: string;
    desired_outcome?: string;
    business_impact?: string;
    annualized_entitlement?: string;
    impact_metrics?: string;
    stakeholders?: string;
    urgency?: string;
    program_name?: string;
    theme?: string;
    assigned_team?: string;
    conversation_summary?: string;
  };
  
  // Indicates if the intake process is complete
  isComplete?: boolean;
  
  // Confidence scores for extracted data (0-1)
  confidence?: {
    [key: string]: number;
  };
  
  // Any errors or warnings
  error?: string;
  warnings?: string[];
  
  // Metadata about the processing
  metadata?: {
    processingTime?: number;
    modelUsed?: string;
    tokensUsed?: number;
  };
}

// Error class for IRIS-specific errors
export class IRISError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public response?: any,
    public errorType?: 'timeout' | 'auth' | 'ratelimit' | 'service' | 'network' | 'unknown'
  ) {
    super(message);
    this.name = 'IRISError';
  }
}

// Error detection utilities
const isTimeoutError = (error: any): boolean => {
  if (error instanceof IRISError) {
    return error.errorType === 'timeout' || 
           error.statusCode === 504 || 
           error.message.toLowerCase().includes('timeout') ||
           error.message.toLowerCase().includes('networkerror');
  }
  
  return error.name === 'TypeError' && 
         error.message.toLowerCase().includes('networkerror');
};

const isAuthError = (error: any): boolean => {
  if (error instanceof IRISError) {
    return error.errorType === 'auth' ||
           error.statusCode === 401 || 
           error.statusCode === 403 ||
           error.message.toLowerCase().includes('unauthorized') ||
           error.message.toLowerCase().includes('expired') ||
           error.message.toLowerCase().includes('invalid token');
  }
  return false;
};

const isRateLimitError = (error: any): boolean => {
  if (error instanceof IRISError) {
    return error.errorType === 'ratelimit' ||
           error.statusCode === 429 ||
           error.message.toLowerCase().includes('too many requests') ||
           error.message.toLowerCase().includes('rate limit');
  }
  return false;
};

const isServiceError = (error: any): boolean => {
  if (error instanceof IRISError) {
    return error.errorType === 'service' ||
           !!(error.statusCode && error.statusCode >= 500 && error.statusCode < 600);
  }
  return false;
};

// Generic error message generator for all IRIS use cases
export const getIRISErrorMessage = (error: any, context?: string): string => {
  const contextStr = context || 'the request';
  
  if (isTimeoutError(error)) {
    return `${contextStr} is taking longer than expected. This is normal for complex requests. Please try again.`;
  }
  
  if (isAuthError(error)) {
    return `Authentication expired. We've refreshed your login - please try again.`;
  }
  
  if (isRateLimitError(error)) {
    return `Too many requests. Please wait a moment and try again.`;
  }
  
  if (isServiceError(error)) {
    return `Service temporarily unavailable. Please try again in a few moments.`;
  }
  
  // Generic fallback
  return `Something went wrong with ${contextStr}. Please try again.`;
};

// Token management functions
export const setTemporaryToken = (token: string) => {
  sessionStorage.setItem('IRIS_TEMP_TOKEN', token);
  
};

export const getTemporaryToken = (): string | null => {
  return sessionStorage.getItem('IRIS_TEMP_TOKEN');
};

export const clearTemporaryToken = () => {
  sessionStorage.removeItem('IRIS_TEMP_TOKEN');
  
};

export const hasTemporaryToken = (): boolean => {
  return !!sessionStorage.getItem('IRIS_TEMP_TOKEN');
};

// Smart token refresh function
const refreshTokenAndRetry = async (originalRequest: IRISRequest): Promise<string> => {
  console.log('🔄 AUTH: Refreshing token and retrying IRIS request...');
  
  // Clear all cached tokens to force fresh fetch
  clearTemporaryToken();
  
  try {
    // Force fresh Midway token
    const freshToken = await getMidwayToken();
    
    if (freshToken) {
      console.log('✅ AUTH: Fresh Midway token obtained, retrying request');
      // Store the fresh token temporarily
      setTemporaryToken(freshToken);
      
      // Retry the original request with fresh token
      return await callIRISRich(originalRequest);
    } else {
      throw new Error('Failed to obtain fresh Midway token');
    }
  } catch (error) {
    console.error('❌ AUTH: Token refresh failed:', error);
    throw new IRISError(
      'Authentication failed. Please refresh the page and try again.',
      401,
      error,
      'auth'
    );
  }
};

// Enhanced IRIS service call with smart error handling and token refresh
export const callIRISRich = async (request: IRISRequest, isRetry: boolean = false): Promise<string> => {
  try {
    // Use provided session ID or generate one if not provided
    const sessionId = request.sessionId || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const requestPayload = {
      id: request.agentId,
      inputText: request.userMessage,
      sessionId: sessionId
    };
    
    // Get token with priority: temp token > Midway token > env variable
    const tempToken = getTemporaryToken();
    let authToken = '';
    
    if (tempToken) {
      authToken = tempToken;
    } else {
      try {
        const midwayToken = await getMidwayToken();
        if (midwayToken) {
          authToken = midwayToken;
          
          // Try to decode token for validation
          try {
            const { jwtDecode } = await import('jwt-decode');
            const decoded = jwtDecode(authToken);
          } catch (e) {
            console.warn('⚠️ AUTH: Token decode failed, but proceeding');
          }
        } else {
          throw new Error('No Midway token received');
        }
      } catch (error) {
        console.warn('⚠️ AUTH: Failed to get Midway token, falling back to env variable:', error);
        authToken = process.env.REACT_APP_API_TOKEN || '';
      }
    }
    
    // Get API key headers for authentication
    const apiKeyHeaders = await apiKeyService.getApiKeyHeaders();
    
    // Make request to API
    const apiUrl = process.env.REACT_APP_API_URL;
    const response = await fetch(`${apiUrl}/api/iris`, {
      method: 'POST',
      headers: {
        ...apiKeyHeaders,
        'Authorization': authToken,
        'Content-Encoding': 'amz-1.0',
      },
      body: JSON.stringify(requestPayload)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // Determine error type based on status code and content
      let errorType: IRISError['errorType'] = 'unknown';
      
      if (response.status === 504) {
        errorType = 'timeout';
      } else if (response.status === 401 || response.status === 403) {
        errorType = 'auth';
      } else if (response.status === 429) {
        errorType = 'ratelimit';
      } else if (response.status >= 500) {
        errorType = 'service';
      }
      
      throw new IRISError(
        `IRIS API failed: ${errorText}`,
        response.status,
        errorText,
        errorType
      );
    }
    
    const result = await response.json();
    
    // Extract the response text from the IRIS response structure
    return result.response || JSON.stringify(result);
    
  } catch (error: any) {
    console.error('IRIS Agent Error:', error);
    
    // Handle network errors (CORS, timeout, etc.)
    if (error.name === 'TypeError' && error.message.includes('NetworkError')) {
      const networkError = new IRISError(
        'Network error occurred',
        504,
        error,
        'timeout'
      );
      
      // If this isn't already a retry and it's an auth error, try token refresh
      if (!isRetry && isAuthError(networkError)) {
        try {
          return await refreshTokenAndRetry(request);
        } catch (refreshError) {
          throw refreshError;
        }
      }
      
      throw networkError;
    }
    
    // Handle IRIS errors with potential token refresh
    if (error instanceof IRISError) {
      // If this isn't already a retry and it's an auth error, try token refresh
      if (!isRetry && isAuthError(error)) {
        try {
          return await refreshTokenAndRetry(request);
        } catch (refreshError) {
          throw refreshError;
        }
      }
      
      throw error;
    }
    
    // Handle other errors
    throw new IRISError(
      `Failed to call IRIS agent: ${error.message || 'Unknown error'}`,
      undefined,
      error,
      'unknown'
    );
  }
};

/**
 * Enhanced IRIS service call that returns structured responses
 * This is the new method that should be used going forward
 */
export const callIRISStructured = async (request: IRISRequest): Promise<IRISResponse> => {
  const startTime = Date.now();
  
  try {
    console.log(`🔮 IRIS STRUCTURED CALL: Starting ${request.stage} stage processing`);
    
    // Use provided session ID or generate one if not provided
    const sessionId = request.sessionId || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const requestPayload = {
      id: request.agentId,
      inputText: request.userMessage,
      sessionId: sessionId,
      stage: request.stage,
      intakeData: request.intakeData,
      // Request structured response format
      responseFormat: 'structured'
    };
    
    // Get authentication token
    const tempToken = getTemporaryToken();
    let authToken = '';
    
    if (tempToken) {
      authToken = tempToken;
    } else {
      try {
        const midwayToken = await getMidwayToken();
        if (midwayToken) {
          authToken = midwayToken;
        } else {
          throw new Error('No Midway token received');
        }
      } catch (error) {
        console.warn('⚠️ AUTH: Failed to get Midway token, falling back to env variable:', error);
        authToken = process.env.REACT_APP_API_TOKEN || '';
      }
    }
    
    // Get API key headers for authentication
    const apiKeyHeaders = await apiKeyService.getApiKeyHeaders();
    
    // Make request to API
    const apiUrl = process.env.REACT_APP_API_URL;
    const response = await fetch(`${apiUrl}/api/iris`, {
      method: 'POST',
      headers: {
        ...apiKeyHeaders,
        'Authorization': authToken,
        'Content-Encoding': 'amz-1.0',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestPayload)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new IRISError(
        `IRIS API failed: ${errorText}`,
        response.status,
        errorText
      );
    }
    
    const result = await response.json();
    const processingTime = Date.now() - startTime;
    
    console.log(`✅ IRIS STRUCTURED SUCCESS: Received response for ${request.stage} stage in ${processingTime}ms`);
    
    // Check if the response is already in structured format
    if (result.reply && typeof result.reply === 'string') {
      // Response is already structured
      return {
        ...result,
        metadata: {
          ...result.metadata,
          processingTime
        }
      } as IRISResponse;
    }
    
    // Fallback: Parse legacy response format
    const responseText = result.response || JSON.stringify(result);
    
    // Try to extract structured data from the response
    const structuredResponse: IRISResponse = {
      reply: responseText,
      metadata: {
        processingTime,
        modelUsed: 'legacy-format'
      }
    };
    
    // For intake stage, try to detect completion signals
    if (request.stage === 'intake') {
      const lowerResponse = responseText.toLowerCase();
      structuredResponse.isComplete = 
        lowerResponse.includes("✅ all fields complete") || 
        lowerResponse.includes("ready to summarize") ||
        lowerResponse.includes("intake complete");
    }
    
    return structuredResponse;
    
  } catch (error: any) {
    const processingTime = Date.now() - startTime;
    console.error(`❌ IRIS STRUCTURED ERROR: Failed ${request.stage} stage processing in ${processingTime}ms:`, error);
    
    if (error instanceof IRISError) {
      throw error;
    }
    
    throw new IRISError(
      `Failed to call IRIS agent: ${error.message || 'Unknown error'}`,
      undefined,
      error
    );
  }
};

/**
 * Retry wrapper for IRIS calls with exponential backoff
 */
export const callIRISWithRetry = async (
  request: IRISRequest, 
  maxRetries: number = 3,
  useStructured: boolean = true
): Promise<IRISResponse | string> => {
  let lastError: Error;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🔄 IRIS RETRY: Attempt ${attempt}/${maxRetries} for ${request.stage} stage`);
      
      if (useStructured) {
        return await callIRISStructured(request);
      } else {
        return await callIRISRich(request);
      }
      
    } catch (error: any) {
      lastError = error;
      
      // Don't retry on authentication errors or client errors (4xx)
      if (error instanceof IRISError && error.statusCode && error.statusCode < 500) {
        console.error(`❌ IRIS NON-RETRYABLE ERROR: ${error.message}`);
        throw error;
      }
      
      if (attempt < maxRetries) {
        const delay = Math.pow(2, attempt - 1) * 1000; // Exponential backoff: 1s, 2s, 4s
        console.warn(`⚠️ IRIS RETRY: Attempt ${attempt} failed, retrying in ${delay}ms...`, error.message);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  console.error(`❌ IRIS RETRY EXHAUSTED: All ${maxRetries} attempts failed`);
  throw lastError!;
};
